"use client";

import { House, LogOut, SquareParking } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import posthog from "posthog-js";
import { useEffect } from "react";
import { registerSuperProperties } from "~/lib/analytics";
import { clearAttributionCode } from "~/lib/attribution";
import { authClient } from "~/lib/auth/client";
import {
  getMobileSidebarNavLinks,
  getPublicNavItems,
  HOST_ONBOARDING_HREF,
  NAV_AUTH_HREF,
} from "~/lib/constants/nav";
import { cn } from "~/lib/utils";
import { useNavAuthStore } from "~/store/navAuthStore";
import {
  type NavProfile,
  useDriverEarningsEntry,
  useNavContext,
  useNavProfile,
} from "./useNavAuth";

type Props = {
  profile: NavProfile;
  initialContext: "host" | "driver";
  driverFeaturesEnabled: boolean;
};

function InitialsAvatar({
  firstName,
  lastName,
}: {
  firstName: string;
  lastName: string;
}) {
  const initials = `${firstName[0] ?? ""}${lastName[0] ?? ""}`.toUpperCase();
  return (
    <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[var(--blue)] font-ui text-[14px] font-bold text-white">
      {initials}
    </div>
  );
}

/**
 * The mobile menu, for both auth states. Below `md` this is the only place the
 * nav links live — the header collapses to logo, CTAs and this trigger — so a
 * signed-out visitor gets the public links here rather than an account list.
 */
export function MobileSidebar({
  profile: serverProfile,
  initialContext,
  driverFeaturesEnabled,
}: Props) {
  const pathname = usePathname();
  const profile = useNavProfile(serverProfile);
  const currentContext = useNavContext({ profile, initialContext });
  const showEarnings = useDriverEarningsEntry(!!profile?.isHost);
  const open = useNavAuthStore((s) => s.sidebarOpen);
  const setSidebarOpen = useNavAuthStore((s) => s.setSidebarOpen);
  const close = () => setSidebarOpen(false);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleSignOut = async () => {
    close();
    await authClient.signOut();
    try {
      posthog.reset();
      // `reset()` clears the super properties along with the identity, and the
      // navigation below is what restores them. Re-apply anyway so the
      // `$pageleave` at unload is still tagged.
      registerSuperProperties(posthog);
    } catch {
      /* non-critical */
    }
    clearAttributionCode();
    window.location.href = "/auth";
  };

  const navLinks = profile
    ? getMobileSidebarNavLinks({
        currentContext,
        driverFeaturesEnabled,
        pathname,
        showEarnings,
      })
    : getPublicNavItems();

  const cta: { href: string; label: string; icon: typeof House } | null =
    currentContext === "host"
      ? // In host context, the toggle only makes sense when driver features
        // exist to switch to — otherwise hide it entirely.
        driverFeaturesEnabled
        ? { href: "/parking", label: "Find a Spot", icon: SquareParking }
        : null
      : profile?.isHost
        ? {
            href: "/host/listings",
            label: "Switch to Host",
            icon: House,
          }
        : { href: "/host/onboarding", label: "Become a Host", icon: House };

  if (!open) return null;

  return (
    <>
      {/* Full-screen panel — sits above everything */}
      <div
        className={cn(
          "fixed inset-0 z-[9999] flex flex-col bg-white",
          pathname.startsWith("/search") ? "top-14" : "top-16",
        )}
        aria-modal="true"
        role="dialog"
        aria-label="Navigation menu"
      >
        {/* ── User header ── */}
        {profile && (
          <div className="flex items-center gap-4 border-b border-[var(--ink-10)] px-6 py-5">
            <InitialsAvatar
              firstName={profile.firstName}
              lastName={profile.lastName}
            />
            <div className="min-w-0 flex-1">
              <p className="truncate font-ui text-[15px] font-semibold text-[var(--ink)]">
                {profile.firstName} {profile.lastName}
              </p>
              <p className="truncate font-ui text-[13px] text-[var(--ink-60)]">
                {profile.email}
              </p>
            </div>
          </div>
        )}

        {/* ── Nav links ── */}
        <nav className="flex-1 overflow-y-auto">
          {navLinks.map(({ href, label, icon: Icon }) => {
            const isActive =
              pathname === href || pathname.startsWith(`${href}/`);
            return (
              <Link
                key={href}
                href={href}
                onClick={close}
                className={cn(
                  "flex items-center gap-4 px-6 py-[18px] font-ui text-[15px] font-medium transition-colors",
                  "border-b border-[var(--ink-05)]",
                  isActive
                    ? "text-[var(--blue)] bg-[var(--blue-xpale)]"
                    : "text-[var(--ink)] hover:bg-[var(--ink-05)]",
                )}
                prefetch={false}
              >
                <Icon
                  className={cn(
                    "size-5 shrink-0",
                    isActive ? "text-[var(--blue)]" : "text-[var(--ink-60)]",
                  )}
                />
                {label}
              </Link>
            );
          })}
        </nav>

        {/* ── Footer actions ── */}
        <div className="space-y-2 border-t border-[var(--ink-10)] bg-[var(--ink-05)] p-4">
          {profile ? (
            <>
              {cta && (
                <Link
                  href={cta.href}
                  onClick={close}
                  className={cn(
                    "flex h-12 w-full items-center justify-center gap-2",
                    "rounded-[var(--r-full)] bg-[var(--blue)] px-4",
                    "font-ui text-[14px] font-semibold text-white",
                    "transition-colors hover:bg-[var(--blue-dark)]",
                  )}
                  prefetch={false}
                >
                  <cta.icon className="size-4 shrink-0" />
                  {cta.label}
                </Link>
              )}

              <button
                type="button"
                onClick={handleSignOut}
                className={cn(
                  "flex h-12 w-full items-center justify-center gap-2",
                  "rounded-[var(--r-full)] border border-[var(--ink-20)] bg-white px-4",
                  "font-ui text-[14px] font-semibold text-[var(--red)]",
                  "transition-colors hover:bg-[var(--red-bg)]",
                )}
              >
                <LogOut className="size-4 shrink-0" />
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                href={HOST_ONBOARDING_HREF}
                onClick={close}
                className={cn(
                  "flex h-12 w-full items-center justify-center",
                  "rounded-[var(--r-full)] bg-[var(--blue)] px-4",
                  "font-ui text-[14px] font-semibold text-white",
                  "transition-colors hover:bg-[var(--blue-dark)]",
                )}
              >
                List your spot
              </Link>
              <Link
                href={NAV_AUTH_HREF}
                onClick={close}
                className={cn(
                  "flex h-12 w-full items-center justify-center",
                  "rounded-[var(--r-full)] border border-[var(--ink-20)] bg-white px-4",
                  "font-ui text-[14px] font-semibold text-[var(--ink)]",
                  "transition-colors hover:bg-[var(--ink-05)]",
                )}
              >
                Sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </>
  );
}
